/**
 * LegalBaseSection Component
 * 
 * Shared base component for legal pages (Privacy Policy and Terms of Service).
 * Ensures consistent layout, spacing, animations, and accessibility across all legal sections.
 */
import { motion } from "framer-motion";
import React, { memo } from "react";

import { LEGAL_ANIMATION } from "../../constants/legalConstants";
import { useTheme } from "../../hooks/useTheme";

interface LegalSectionProps {
  hasBackground?: boolean;
  children: React.ReactNode;
  className?: string;
  sectionId?: string;
  ariaLabel?: string;
}

/**
 * Base component for legal page sections
 * Ensures consistent layout, spacing, animations, and accessibility across all legal sections
 */
const LegalBaseSection: React.FC<LegalSectionProps> = ({ 
  hasBackground = false, 
  children, 
  className = "",
  sectionId,
  ariaLabel
}) => {
  const { themeClasses, isDarkTheme } = useTheme();

  const backgroundClass = hasBackground 
    ? `${isDarkTheme ? 'bg-gray-950' : 'bg-gray-50'}` 
    : '';

  return (
    <section 
      id={sectionId}
      aria-label={ariaLabel}
      className={`section py-12 ${backgroundClass} ${className}`.trim()}
    >
      <div className="container">
        <motion.div
          initial={LEGAL_ANIMATION.initial}
          whileInView={LEGAL_ANIMATION.whileInView}
          viewport={LEGAL_ANIMATION.viewport}
          transition={{ 
            duration: LEGAL_ANIMATION.duration, 
            ease: LEGAL_ANIMATION.ease 
          }}
          className="mx-auto max-w-4xl"
        >
          <div className={`card rounded-lg p-8 md:p-12 ${themeClasses.card}`}>
            {children}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default memo(LegalBaseSection); 